/**
 * MFAGuard — gates the authed app behind MFA when the user has verified
 * factors and the current session is still AAL1.
 *
 * F-024 v2: AAL is now read from the JWT claims (via `useAuth().needsMfa`),
 * not from the Supabase `Session` object (which doesn't carry it). The
 * legacy client-side trusted-device fingerprint check is gone — server-side
 * `custom_access_token_hook` mints aal=aal2 directly when the
 * trust-redeem edge function consumes a valid cookie, so the JWT is the
 * authoritative signal.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useListMfaFactorsQuery } from "../api/authApi";
import { MFALoginFlow } from "./MFALoginFlow";
import type { MfaState } from "../types/authTypes";

type Transition = { [from in MfaState]?: { [event in MfaState]?: MfaState } };

const MFA_TRANSITIONS: Transition = {
  INITIALIZING: {
    MFA_NOT_NEEDED: "MFA_NOT_NEEDED",
    MFA_REQUIRED: "MFA_REQUIRED",
    ERROR: "ERROR",
  },
  MFA_NOT_NEEDED: {},
  MFA_REQUIRED: {
    MFA_VERIFYING: "MFA_VERIFYING",
    MFA_VERIFIED: "MFA_VERIFIED",
    MFA_FAILED: "MFA_FAILED",
    ERROR: "ERROR",
    // Late-arriving trusted-device or AAL2 signal can clear a pending MFA
    // requirement; the gate was over-strict before and trapped users in
    // MFA_REQUIRED even after their session was upgraded.
    MFA_NOT_NEEDED: "MFA_NOT_NEEDED",
  },
  MFA_VERIFYING: {
    MFA_VERIFIED: "MFA_VERIFIED",
    MFA_FAILED: "MFA_FAILED",
    MFA_REQUIRED: "MFA_REQUIRED",
    ERROR: "ERROR",
  },
  MFA_VERIFIED: { MFA_NOT_NEEDED: "MFA_NOT_NEEDED" },
  MFA_FAILED: { MFA_REQUIRED: "MFA_REQUIRED", ERROR: "ERROR" },
  ERROR: {
    MFA_REQUIRED: "MFA_REQUIRED",
    MFA_NOT_NEEDED: "MFA_NOT_NEEDED",
  },
};

interface MFAGuardProps {
  children: React.ReactNode;
  user: User | null | undefined;
  onMFAComplete?: () => void;
}

// Session-scoped MFA-pass latch. Observed in the wild (logs 2026-04-19):
// after a successful verify, MFAGuard was being unmounted + remounted
// (likely via a session-ref change propagating through AuthContext),
// which wiped the React-local `mfaPassed` state. Result: popup reappeared
// seconds after the green success box. Persisting the latch to
// sessionStorage keeps the "already verified" signal alive across those
// remounts while still clearing it on sign-out (see SIGNED_OUT handler
// in AuthContext).
const MFA_PASS_STORAGE_KEY = "flexwise.mfa.passed";

function readMfaPassed(): boolean {
  try {
    return sessionStorage.getItem(MFA_PASS_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMfaPassed(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(MFA_PASS_STORAGE_KEY, "1");
    else sessionStorage.removeItem(MFA_PASS_STORAGE_KEY);
  } catch {
    // Best-effort only.
  }
}

export function clearMfaPassedLatch(): void {
  writeMfaPassed(false);
}

export function MFAGuard({ children, user, onMFAComplete }: MFAGuardProps) {
  const { needsMfa } = useAuth();
  const [mfaState, setMfaState] = useState<MfaState>("INITIALIZING");
  const [mfaPassed, setMfaPassedState] = useState<boolean>(() =>
    readMfaPassed(),
  );
  const setMfaPassed = useCallback((value: boolean) => {
    writeMfaPassed(value);
    setMfaPassedState(value);
  }, []);
  const navigate = useNavigate();
  const periodicCheckRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const transition = useCallback((toState: MfaState) => {
    setMfaState((prev) => {
      if (prev === toState) return prev;
      const allowed = MFA_TRANSITIONS[prev];
      if (!allowed || !allowed[toState]) {
        console.error(
          `🔒 MFA State Machine: Invalid transition from ${prev} to ${toState}`,
        );
        return prev;
      }
      return toState;
    });
  }, []);

  // F-024 v2: legacy `useTrustedDevice` + Netlify cookie checks are gone.
  // The custom_access_token_hook now mints aal=aal2 directly when the
  // server-side trusted-device cookie redeems a valid row — so the JWT
  // claim is authoritative. `needsMfa` from AuthContext decodes the
  // claim correctly (whereas `session.aal` from the SDK is always
  // undefined because Supabase's Session type doesn't carry it).
  const factorsQuery = useListMfaFactorsQuery(undefined, {
    skip: !user || !needsMfa,
  });

  // Drive the state machine from the resolved hook states.
  useEffect(() => {
    if (!user) {
      transition("MFA_NOT_NEEDED");
      return;
    }

    // Source of truth: needsMfa is computed from the live JWT claims.
    // True ⇒ user has enrolled factors but JWT is still aal1.
    // False ⇒ no factors, OR aal2 already (real TOTP verify, or hook
    //         minted aal2 via trusted-device redemption).
    if (!needsMfa) {
      transition("MFA_NOT_NEEDED");
      return;
    }

    if (factorsQuery.isLoading || factorsQuery.isFetching) return;
    if (factorsQuery.error) {
      transition("ERROR");
      return;
    }

    const hasMfa = !!factorsQuery.data?.hasMfa;
    transition(hasMfa ? "MFA_REQUIRED" : "MFA_NOT_NEEDED");
  }, [
    user,
    needsMfa,
    factorsQuery.data,
    factorsQuery.error,
    factorsQuery.isLoading,
    factorsQuery.isFetching,
    transition,
  ]);

  // Periodic AAL re-check when MFA is required — handles delayed session
  // upgrades (e.g. user verified in another tab).
  useEffect(() => {
    if (mfaState !== "MFA_REQUIRED") {
      if (periodicCheckRef.current) {
        window.clearInterval(periodicCheckRef.current);
        periodicCheckRef.current = null;
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }
    if (periodicCheckRef.current) return;
    periodicCheckRef.current = window.setInterval(async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        // session.aal isn't on the Supabase type; AAL is in the JWT claims
        // (`access_token`). Decode the payload to read it.
        let aal: string | null = null;
        const token = session?.access_token;
        if (token) {
          try {
            const payload = JSON.parse(
              atob(
                token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"),
              ),
            ) as { aal?: string };
            aal = payload.aal ?? null;
          } catch {
            aal = null;
          }
        }
        if (aal === "aal2") {
          transition("MFA_VERIFIED");
          onMFAComplete?.();
          window.setTimeout(() => transition("MFA_NOT_NEEDED"), 100);
        }
      } catch (err) {
        console.error("🔒 MFA periodic check error:", err);
      }
    }, 2000);
    timeoutRef.current = window.setTimeout(() => {
      if (periodicCheckRef.current) {
        window.clearInterval(periodicCheckRef.current);
        periodicCheckRef.current = null;
      }
    }, 30000);
    return () => {
      if (periodicCheckRef.current) {
        window.clearInterval(periodicCheckRef.current);
        periodicCheckRef.current = null;
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [mfaState, onMFAComplete, transition]);

  if (mfaState === "INITIALIZING") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Überprüfe Zwei-Faktor-Authentifizierung...</p>
          <p className="text-sm text-gray-400 mt-2">State: {mfaState}</p>
        </div>
      </div>
    );
  }

  // If we've already passed MFA this session, render children regardless
  // of what the state machine says — guards against late refetches that
  // would otherwise re-derive MFA_REQUIRED.
  if (mfaPassed) {
    return <>{children}</>;
  }

  const showFlow =
    mfaState === "MFA_REQUIRED" ||
    mfaState === "MFA_VERIFYING" ||
    mfaState === "MFA_FAILED" ||
    mfaState === "ERROR";

  if (showFlow) {
    return (
      <MFALoginFlow
        user={user}
        onComplete={async (success: boolean) => {
          if (success) {
            // Run the parent's post-verify hook FIRST so the profile
            // refetch + role-aware navigation finish before MFAGuard
            // hands control back. Otherwise mfaPassed flips, the inner
            // Routes render against a stale dashboardPath (still '/'
            // because userProfile hadn't loaded yet), and React-Router
            // ends up at `/` with no matching dashboard render — a
            // white screen until the user manually refreshes.
            try {
              await onMFAComplete?.();
            } catch {
              // best-effort
            }
            // Latch (sessionStorage-backed) so a post-verify remount
            // can't wipe the pass signal and re-show the popup.
            setMfaPassed(true);
            transition("MFA_NOT_NEEDED");
          } else {
            // Abbrechen: client-side bypass. Lets the user into the app on
            // aal1 so they can verify whether DB-level gates actually
            // enforce MFA. NOT a real security boundary — RLS should
            // treat aal1 as untrusted for sensitive tables. Kept here as
            // a diagnostic escape hatch until RLS is audited.
            setMfaPassed(true);
            navigate("/", { replace: true });
          }
        }}
      />
    );
  }

  if (mfaState === "MFA_NOT_NEEDED") {
    return <>{children}</>;
  }

  return null;
}
